/**
 * @fileoverview Утилиты для замены placeholder-ключей участников в документе
 * @description Позволяет маппить короткие placeholder-коды из шаблона
 * на реальные participant_type_code при создании нотариального действия
 */

import { generateDisplayKey } from "./keyGenerator";
import { SPECIAL_SERVICE_CODES } from "./constants";

/**
 * Заменяет participant_type_code во всех mention узлах документа
 * и пересчитывает displayKey
 *
 * @param {Object} documentJson - JSON структура документа TipTap
 * @param {string} oldCode - Старый код (placeholder)
 * @param {string} newCode - Новый код (реальный participant_type_code)
 * @returns {Object} Обновленный JSON документ
 */
export function replaceParticipantCode(documentJson, oldCode, newCode) {
  if (!documentJson || !documentJson.content) {
    return documentJson;
  }

  if (!oldCode || !newCode || oldCode === newCode) {
    return documentJson;
  }

  const result = JSON.parse(JSON.stringify(documentJson));

  function traverse(node) {
    if (!node || typeof node !== "object") return;

    if (node.type === "mention" && node.attrs) {
      if (node.attrs.participant_type_code === oldCode) {
        node.attrs.participant_type_code = newCode;

        if (node.attrs.service_code) {
          node.attrs.displayKey = generateDisplayKey(
            node.attrs.service_code,
            node.attrs.key,
            newCode
          );
        }
      }
    }

    if (Array.isArray(node.content)) {
      node.content.forEach(traverse);
    }
  }

  traverse(result);

  return result;
}

/**
 * Заменяет participant_type_code во всех языковых версиях документа
 *
 * @param {Object} form - Объект формы с content, content_kz, content_en
 * @param {string} oldCode - Старый код (placeholder)
 * @param {string} newCode - Новый код (реальный participant_type_code)
 * @returns {Object} Обновленная форма
 */
export function replaceParticipantCodeInAllLanguages(form, oldCode, newCode) {
  if (!form || typeof form !== "object") {
    return form;
  }

  const updatedForm = { ...form };
  const languages = ["content", "content_kz", "content_en"];

  languages.forEach((key) => {
    const documentJson = updatedForm[key];
    if (documentJson && typeof documentJson === "object") {
      updatedForm[key] = replaceParticipantCode(documentJson, oldCode, newCode);
    }
  });

  return updatedForm;
}

/**
 * Находит все уникальные participant_type_code в документе,
 * которые не совпадают ни с одним реальным кодом участника
 *
 * @param {Object} documentJson - JSON структура документа TipTap
 * @param {string[]} realParticipantCodes - Коды реальных участников + спецсервисов
 * @returns {string[]} Массив placeholder-кодов
 */
export function findUnmappedPlaceholders(documentJson, realParticipantCodes) {
  if (!documentJson || !documentJson.content) {
    return [];
  }

  const codes = new Set(realParticipantCodes || []);
  const placeholders = new Set();

  // Специальные сервисы не используют participant_type_code
  const specialServices = Object.values(SPECIAL_SERVICE_CODES);

  function traverse(node) {
    if (!node || typeof node !== "object") return;

    if (node.type === "mention" && node.attrs) {
      const ptc = node.attrs.participant_type_code;
      const serviceCode = node.attrs.service_code;

      // Пропускаем: нет кода, кастомные, нотариус, спецсервисы
      if (
        !ptc ||
        node.attrs.isCustom ||
        node.attrs.isNotary ||
        specialServices.includes(serviceCode)
      ) {
        return;
      }

      // Если код не совпадает ни с одним реальным — это placeholder
      if (!codes.has(ptc)) {
        placeholders.add(ptc);
      }
    }

    if (Array.isArray(node.content)) {
      node.content.forEach(traverse);
    }
  }

  traverse(documentJson);

  return [...placeholders];
}

/**
 * Клонирует RepeatSection с placeholder и заменяет placeholder на реальный код.
 * - Если это первый участник: заменяет placeholder прямо в существующей секции.
 * - Если секция уже была заменена: клонирует оригинал (из шаблона) и вставляет ниже.
 *
 * @param {Object} documentJson - JSON TipTap документа
 * @param {string} placeholderCode - Код-заменитель (например "nas")
 * @param {string} realCode - Реальный participant_type_code
 * @param {string} label - Читаемое название участника
 * @param {Object|null} originalTemplate - Оригинальная секция-шаблон (для клонирования)
 * @returns {{ document: Object, template: Object|null }} Обновленный документ + шаблон
 */
export function mapRepeatSection(
  documentJson,
  placeholderCode,
  realCode,
  label,
  originalTemplate
) {
  if (!documentJson || !documentJson.content) {
    return { document: documentJson, template: originalTemplate };
  }

  const result = JSON.parse(JSON.stringify(documentJson));

  // Находим ВСЕ секции с данным placeholderCode
  const sectionIndices = [];
  result.content.forEach((node, idx) => {
    if (
      node.type === "repeatSection" &&
      node.attrs?.placeholderCode === placeholderCode
    ) {
      sectionIndices.push(idx);
    }
  });

  if (sectionIndices.length === 0 && originalTemplate?.nodes) {
    // Секции уже были заменены — вставляем клоны после каждой секции с matching sectionOrder
    let offset = 0;
    originalTemplate.nodes.forEach((savedNode, i) => {
      // Ищем последнюю секцию с sectionOrder === i
      let targetIdx = -1;
      result.content.forEach((node, idx) => {
        if (node.type === "repeatSection" && node.attrs?.sectionOrder === i) {
          targetIdx = idx;
        }
      });

      const clone = JSON.parse(JSON.stringify(savedNode));
      clone.attrs = {
        ...clone.attrs,
        placeholderCode: "",
        sectionOrder: i,
        label: label || realCode,
      };
      replaceMentionsInNode(clone, placeholderCode, realCode);

      if (targetIdx !== -1) {
        // Вставляем сразу после последней секции с таким же sectionOrder
        result.content.splice(targetIdx + 1 + offset, 0, clone);
      } else {
        // Fallback: вставляем в конец
        result.content.push(clone);
      }
      offset++;
    });

    return { document: result, template: originalTemplate };
  }

  if (sectionIndices.length > 0) {
    // Первая замена — сохраняем оригиналы как шаблон
    const savedTemplate = originalTemplate || {
      nodes: sectionIndices.map((idx) =>
        JSON.parse(JSON.stringify(result.content[idx]))
      ),
      positions: [...sectionIndices],
    };

    // Заменяем ВСЕ найденные секции + помечаем sectionOrder
    sectionIndices.forEach((idx, i) => {
      result.content[idx].attrs = {
        ...result.content[idx].attrs,
        placeholderCode: "",
        sectionOrder: i,
        label: label || realCode,
      };
      replaceMentionsInNode(result.content[idx], placeholderCode, realCode);
    });

    return { document: result, template: savedTemplate };
  }

  // Нет RepeatSection — просто делаем обычную замену
  return {
    document: replaceParticipantCode(documentJson, placeholderCode, realCode),
    template: originalTemplate,
  };
}

/**
 * mapRepeatSection для всех языковых версий
 * Шаблон секции хранится в form._repeatSectionTemplate (переживает F5)
 */
export function mapRepeatSectionInAllLanguages(
  form,
  placeholderCode,
  realCode,
  label
) {
  if (!form || typeof form !== "object") {
    return form;
  }

  const updatedForm = { ...form };

  // Инициализируем хранилище шаблона если нет
  if (!updatedForm._repeatSectionTemplate) {
    updatedForm._repeatSectionTemplate = {};
  }

  const templates = updatedForm._repeatSectionTemplate;
  const languages = ["content", "content_kz", "content_en"];

  languages.forEach((key) => {
    const doc = updatedForm[key];
    if (doc && typeof doc === "object") {
      const result = mapRepeatSection(
        doc,
        placeholderCode,
        realCode,
        label,
        templates[key] || null
      );
      updatedForm[key] = result.document;
      if (result.template) {
        templates[key] = result.template;
      }
    }
  });

  updatedForm._repeatSectionTemplate = templates;

  return updatedForm;
}

/**
 * Восстанавливает оригинальную RepeatSection с placeholder из сохранённого шаблона
 * Используется когда все участники удалены
 */
export function restoreOriginalRepeatSection(form) {
  if (!form || !form._repeatSectionTemplate) {
    return form;
  }

  const updatedForm = { ...form };
  const templates = updatedForm._repeatSectionTemplate;
  const languages = ["content", "content_kz", "content_en"];

  languages.forEach((key) => {
    const doc = updatedForm[key];
    const template = templates[key];

    if (doc && doc.content && template) {
      const result = JSON.parse(JSON.stringify(doc));

      // Проверяем: есть ли уже repeatSection в документе
      const hasSection = result.content.some((n) => n.type === "repeatSection");

      if (!hasSection && template.nodes?.length) {
        // Вставляем все оригинальные секции на их позиции
        const positions = template.positions || [];
        template.nodes.forEach((savedNode, i) => {
          const clone = JSON.parse(JSON.stringify(savedNode));
          const position = positions[i];
          const insertAt = Math.min(
            position ?? result.content.length,
            result.content.length
          );
          result.content.splice(insertAt + i, 0, clone);
        });
      }

      updatedForm[key] = result;
    }
  });

  return updatedForm;
}

/**
 * Заменяет participant_type_code и displayKey в mention-ах внутри узла
 */
function replaceMentionsInNode(node, oldCode, newCode) {
  if (!node || typeof node !== "object") return;

  if (node.type === "mention" && node.attrs) {
    if (node.attrs.participant_type_code === oldCode) {
      node.attrs.participant_type_code = newCode;
      if (node.attrs.service_code) {
        node.attrs.displayKey = generateDisplayKey(
          node.attrs.service_code,
          node.attrs.key,
          newCode
        );
      }
    }
  }

  if (Array.isArray(node.content)) {
    node.content.forEach((child) =>
      replaceMentionsInNode(child, oldCode, newCode)
    );
  }
}

/**
 * Удаляет RepeatSection из документа по participant_type_code
 * Используется при откреплении участника от НД
 *
 * @param {Object} documentJson - JSON TipTap документа
 * @param {string} participantCode - participant_type_code удалённого участника
 * @returns {Object} Обновленный документ
 */
export function removeRepeatSectionByCode(documentJson, participantCode) {
  if (!documentJson || !documentJson.content || !participantCode) {
    return documentJson;
  }

  const result = JSON.parse(JSON.stringify(documentJson));

  result.content = result.content.filter((node) => {
    if (node.type !== "repeatSection") return true;

    // Проверяем: есть ли внутри секции mention с данным participant_type_code
    let hasCode = false;
    function check(n) {
      if (!n || typeof n !== "object") return;
      if (
        n.type === "mention" &&
        n.attrs?.participant_type_code === participantCode
      ) {
        hasCode = true;
      }
      if (Array.isArray(n.content)) n.content.forEach(check);
    }
    check(node);

    return !hasCode;
  });

  return result;
}

/**
 * Удаляет RepeatSection для всех языковых версий
 */
export function removeRepeatSectionByCodeInAllLanguages(form, participantCode) {
  if (!form || typeof form !== "object") return form;

  const updatedForm = { ...form };
  const languages = ["content", "content_kz", "content_en"];

  languages.forEach((key) => {
    const doc = updatedForm[key];
    if (doc && typeof doc === "object") {
      updatedForm[key] = removeRepeatSectionByCode(doc, participantCode);
    }
  });

  return updatedForm;
}
